import React from 'react'

import {omit} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import invariant from 'invariant'
import * as Yup from 'yup'

import {useAppSelector} from '../../hooks/redux'

import useAsync from '../../../../commons/src/hooks/use-async'
import {captureException} from '../../../../commons/src/services/logging'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import * as providerSpecializationApi from '../../services/api/provider-specialization'
import {toTouchedFormData} from '../utils/form'

import {ProviderSpecializationOption} from '../../../../commons/src/types'
import {ProviderId} from '../../types'

import checkedIcon from '../../../../commons/src/assets/icons/checked.svg'
import refreshIcon from '../../../../commons/src/assets/icons/refresh.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import Button from '../../../../commons/src/components/button'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import LabeledCheckbox from '../../../../commons/src/components/labeled-checkbox'
import LinkButton from '../../../../commons/src/components/link-button'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import Paragraph from '../../../../commons/src/components/paragraph'
import Section from '../../../../commons/src/components/section'
import Stack from '../../../../commons/src/components/stack'

export const specializationsOptions: {id: ProviderSpecializationOption; name: string}[] = [
  {id: 'rapid_prototyping', name: 'Rapid prototyping'},
  {id: 'small_batch_production', name: 'Small batch production'},
  {id: 'large_batch_production', name: 'Large batch production'},
  {id: 'end_use_parts', name: 'End-Use parts'},
  {id: 'miniatures_and_figurines', name: 'Miniatures and figurines'},
  {id: 'large_size_manufacturing', name: 'Large size manufacturing'},
]

const initialSpecializationValues: Record<ProviderSpecializationOption, boolean> = {
  rapid_prototyping: false,
  small_batch_production: false,
  large_batch_production: false,
  end_use_parts: false,
  miniatures_and_figurines: false,
  large_size_manufacturing: false,
}

type Props = {
  providerId: ProviderId
}

const SettingsProfileSpecializationsPartial = ({providerId}: Props) => {
  const [isProviderSpecializationsSubmitted, setIsProviderSpecializationsSubmitted] =
    React.useState(false)

  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const {
    isLoading: isLoadingProviderSpecializations,
    data: providerSpecializations = [],
    refresh: receiveProviderSpecializations,
    error: providerSpecializationError,
  } = useAsync(() => providerSpecializationApi.getByProvider(provider.id), [], {
    onError: error => {
      captureException(error)
    },
  })

  const memoizedInitialSpecificationValues = React.useMemo(
    () => ({
      ...initialSpecializationValues,
      ...providerSpecializations.reduce(
        (acc, specialization) => {
          acc[specialization.specialization] = true
          return acc
        },
        {} as Record<ProviderSpecializationOption, boolean>
      ),
      formikGeneral: '',
    }),
    [providerSpecializations]
  )

  const providerSpecializationsFormik = useFormik<
    Record<ProviderSpecializationOption, boolean> & {formikGeneral: string}
  >({
    enableReinitialize: true,
    initialValues: {
      ...initialSpecializationValues,
      ...providerSpecializations.reduce(
        (acc, specialization) => {
          acc[specialization.specialization] = true
          return acc
        },
        {} as Record<ProviderSpecializationOption, boolean>
      ),
      formikGeneral: '',
    },
    validationSchema: Yup.object({
      ...Object.keys(initialSpecializationValues).reduce(
        (acc, key) => {
          acc[key] = Yup.boolean().required()
          return acc
        },
        {} as Record<string, Yup.Schema<boolean>>
      ),
    }),
    onSubmit: async (values, actions) => {
      const touchedValues = omit(toTouchedFormData(values, memoizedInitialSpecificationValues), [
        'formikGeneral',
      ]) as Record<string, boolean>

      const idsToDelete = providerSpecializations
        .filter(item => touchedValues[item.specialization] === false)
        .map(item => item.id)

      const specializationsToCreate = Object.keys(touchedValues)
        .filter(key => touchedValues[key])
        .map(key => ({
          specialization: key as ProviderSpecializationOption,
          provider: providerId,
        }))

      try {
        await Promise.all([
          providerSpecializationApi.removeMultiple(idsToDelete),
          providerSpecializationApi.createMultiple(specializationsToCreate),
        ])

        setIsProviderSpecializationsSubmitted(true)
        receiveProviderSpecializations()
      } catch (error) {
        captureException(error)
        actions.setFieldError('formikGeneral', `Update failed with: ${(error as Error).message}`)
      }
    },
  })

  return (
    <Section>
      <Stack>
        <Headline label="Specializations" className="u-no-margin-bottom" />
        <Paragraph className="u-no-margin-bottom">
          Please select the areas of expertise that best fits your company.
        </Paragraph>
        {isLoadingProviderSpecializations && (
          <div>
            <LoadingIndicator />
          </div>
        )}
        {!isLoadingProviderSpecializations && providerSpecializationError && (
          <ActionBar>
            <Paragraph size="xs" warning className="u-no-margin-bottom">
              Loading your current specialization settings failed
            </Paragraph>
            <LinkButton
              icon={<Icon source={refreshIcon} />}
              onClick={() => {
                receiveProviderSpecializations()
              }}
            />
          </ActionBar>
        )}
        {!isLoadingProviderSpecializations && !providerSpecializationError && (
          <form onSubmit={providerSpecializationsFormik.handleSubmit}>
            {specializationsOptions.map(specialization => (
              <LabeledCheckbox
                key={specialization.id}
                tiny
                baseline
                name={specialization.id}
                label={specialization.name}
                checked={providerSpecializationsFormik.values[specialization.id]}
                error={
                  providerSpecializationsFormik.submitCount > 0 &&
                  !!providerSpecializationsFormik.errors[specialization.id]
                }
                onChange={(value, name) => {
                  providerSpecializationsFormik.setFieldValue(name as string, value)
                  if (isProviderSpecializationsSubmitted) {
                    setIsProviderSpecializationsSubmitted(false)
                  }
                }}
                onBlur={providerSpecializationsFormik.handleBlur}
              />
            ))}
            {providerSpecializationsFormik.errors.formikGeneral && (
              <Paragraph error>{providerSpecializationsFormik.errors.formikGeneral}</Paragraph>
            )}
          </form>
        )}
        <ActionBar>
          <Button
            disabled={
              providerSpecializationsFormik.isSubmitting ||
              isProviderSpecializationsSubmitted ||
              !providerSpecializationsFormik.dirty
            }
            icon={
              isProviderSpecializationsSubmitted ? (
                <Icon source={checkedIcon} />
              ) : providerSpecializationsFormik.isSubmitting &&
                providerSpecializationsFormik.submitCount > 0 &&
                isEmpty(providerSpecializationsFormik.errors) ? (
                <LoadingIndicator invert />
              ) : undefined
            }
            onClick={preventDoubleClick(() => providerSpecializationsFormik.handleSubmit())}
          >
            {isProviderSpecializationsSubmitted ? 'Saved' : 'Save'}
          </Button>
          <Button
            hierarchy="secondary"
            disabled={
              providerSpecializationsFormik.isSubmitting ||
              isProviderSpecializationsSubmitted ||
              !providerSpecializationsFormik.dirty
            }
            onClick={preventDoubleClick(() => {
              providerSpecializationsFormik.resetForm()
            })}
          >
            Reset
          </Button>
        </ActionBar>
      </Stack>
    </Section>
  )
}

export default SettingsProfileSpecializationsPartial
